import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input, Label, Button, Toggle } from '../../ui';
import { PremiumBrandingAlert } from '../portal/PremiumBrandingAlert';
import { useUIStore } from '../../../store/uiStore';
import { updateHelpCenterSettings } from '../../../services/api';
import toast from 'react-hot-toast';
import { Copy } from 'lucide-react';
import { generate } from 'random-words';
import { debounce } from 'lodash';

interface HelpCenterPasswordSettingsProps {
  settings?: {
    password?: string;
    passwordEnabled?: boolean;
    passwordValidDays?: number;
  };
  isProCloudPlan: boolean;
  onUpdate: (settings: {
    password?: string;
    passwordEnabled?: boolean;
    passwordValidDays?: number;
  }) => Promise<void>;
}

export const HelpCenterPasswordSettings: React.FC<HelpCenterPasswordSettingsProps> = ({
  settings,
  isProCloudPlan,
  onUpdate,
}) => {
  const { setIsSaving } = useUIStore();
  const [localSettings, setLocalSettings] = useState({
    password: settings?.password || '',
    passwordEnabled: settings?.passwordEnabled || false,
    passwordValidDays: settings?.passwordValidDays || 0,
  });
  const localSettingsRef = useRef(localSettings);
  const debouncedSyncRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    localSettingsRef.current = localSettings;
  }, [localSettings]);

  // Update local settings when props change
  useEffect(() => {
    if (settings) {
      setLocalSettings({
        password: settings.password || '',
        passwordEnabled: settings.passwordEnabled || false,
        passwordValidDays: settings.passwordValidDays || 0,
      });
    }
  }, [settings]);

  const generatePassphrase = (numWords = 4, separator = "-") => {
    const words = generate({ exactly: numWords });
    return Array.isArray(words) ? words.join(separator) : words;
  };

  // Create the sync function that always gets the latest localSettings
  const syncSettings = useCallback(async () => {
    const currentSettings = localSettingsRef.current;
    if (!currentSettings) return;

    setIsSaving(true);
    try {
      await onUpdate({
        password: currentSettings.passwordEnabled ? currentSettings.password : undefined,
        passwordEnabled: currentSettings.passwordEnabled,
        passwordValidDays: currentSettings.passwordValidDays,
      });
    } catch (error) {
      console.error('Error updating password settings:', error);
      toast.error('Failed to update password settings', {
        position: 'top-center',
        duration: 3000,
      });
    } finally {
      setTimeout(() => setIsSaving(false), 400);
    }
  }, [onUpdate, setIsSaving]);

  // Create debounced version once
  useEffect(() => {
    debouncedSyncRef.current = debounce(syncSettings, 300);

    // Cleanup on unmount
    return () => {
      if (debouncedSyncRef.current) {
        debouncedSyncRef.current.cancel();
      }
    };
  }, [syncSettings]);

  const handleSettingChange = (updates: Partial<typeof localSettings>) => {
    const updatedSettings = { ...localSettings, ...updates };
    setLocalSettings(updatedSettings);
    debouncedSyncRef.current?.();
  };

  const handleTogglePasswordEnabled = () => {
    const newPasswordEnabled = !localSettings.passwordEnabled;
    const updates: Partial<typeof localSettings> = {
      passwordEnabled: newPasswordEnabled,
    };

    // Auto-generate password when enabling
    if (newPasswordEnabled && !localSettings.password) {
      updates.password = generatePassphrase();
    }

    handleSettingChange(updates);
  };

  const handleCopyPassword = () => {
    if (!localSettings.password) return;
    navigator.clipboard.writeText(localSettings.password);
    toast.success('Password copied to clipboard', {
      position: 'top-center',
      duration: 3000,
    });
  };

  return (
    <div className="space-y-6">
      {!isProCloudPlan && (
        <PremiumBrandingAlert context="help-center" />
      )}
      <div
        className={`${
          !isProCloudPlan ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <div className="space-y-4">
          <div className="space-y-2 flex flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <Label htmlFor="passwordEnabled">Password Protection</Label>
              <p className="text-[0.8rem] text-muted-foreground">
                Require a password to access this help center
              </p>
            </div>
            <div className="ml-10 flex justify-between items-center">
              <Toggle
                checked={localSettings.passwordEnabled}
                onChange={handleTogglePasswordEnabled}
              />
            </div>
          </div>

          {localSettings.passwordEnabled && (
            <>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="flex items-start justify-start gap-2 mt-1">
                  <Input
                    id="password"
                    type="text"
                    value={localSettings.password || ""}
                    onChange={(e) =>
                      handleSettingChange({ password: e.target.value })
                    }
                    maxLength={60}
                    className="h-8 text-gray-500 text-sm border rounded px-2 !w-[350px]"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    className="ml-2 inline-flex items-center justify-center whitespace-nowrap hover:bg-gray-50 font-medium border bg-background shadow-sm h-8 rounded px-3 text-xs transition-colors duration-200 hover:shadow-md"
                    onClick={handleCopyPassword}
                  >
                    <Copy size={16} className="mr-2" />
                    Copy
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="validDays">Password Valid For (Days)</Label>
                <Input
                  id="validDays"
                  type="number"
                  min="0"
                  value={localSettings.passwordValidDays}
                  onChange={(e) =>
                    handleSettingChange({
                      passwordValidDays: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="0"
                  className="mt-1 w-32"
                />
                <p className="text-[0.8rem] text-muted-foreground mt-1">
                  Number of days the password token is valid. Set to 0 for unlimited (token never expires).
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

